import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, Party, PartyStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const REGISTRATION_ROLES = ['REGISTRATION_OFFICER', 'ELECTORAL_COMMISSIONER'];
const COMMISSIONER_ROLES = ['ELECTORAL_COMMISSIONER'];
const STATUSES: PartyStatus[] = ['ACTIVE', 'SUSPENDED', 'DEREGISTERED'];

/** GET/POST /api/v1/m39/parties — registration desk creates, only the Commissioner changes standing. */
export function Parties() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canRegister = roles.some((r) => REGISTRATION_ROLES.includes(r));
  const isCommissioner = roles.some((r) => COMMISSIONER_ROLES.includes(r));
  const [status, setStatus] = useState<PartyStatus | ''>('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [reasonFor, setReasonFor] = useState<{ party: Party; action: 'suspend' | 'deregister' } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const params = new URLSearchParams({ limit: '50' });
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  const listQ = useApiQuery<Page<Party>>(`/api/v1/m39/parties?${params.toString()}`);

  const reactivate = async (party: Party) => {
    setBusy(party.id);
    setActionError(null);
    try {
      await api.post(`/api/v1/m39/parties/${party.id}/reactivate`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Reactivation failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Political Parties"
        subtitle="The party register — registration, suspension, reactivation, and deregistration."
        actions={
          canRegister ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Register party
            </button>
          ) : undefined
        }
      />
      {!canRegister ? <RoleNotice roles={REGISTRATION_ROLES} /> : null}

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Name or abbreviation contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Progressive…" />
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as PartyStatus | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No parties match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Name</th>
                <th className="table-head">Abbrev.</th>
                <th className="table-head">Leader NIN</th>
                <th className="table-head">Registered</th>
                <th className="table-head">Status</th>
                {isCommissioner ? <th className="table-head" /> : null}
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id}>
                  <td className="table-cell font-medium">{p.name}</td>
                  <td className="table-cell text-xs">{p.abbreviation ?? '—'}</td>
                  <td className="table-cell text-xs">{p.leaderNin ?? '—'}</td>
                  <td className="table-cell text-xs">{formatDate(p.registeredAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                  {isCommissioner ? (
                    <td className="table-cell text-right">
                      <div className="flex justify-end gap-1.5">
                        {p.status === 'ACTIVE' ? (
                          <button className="btn-secondary !px-2.5 !py-1 text-xs" disabled={busy !== null} onClick={() => setReasonFor({ party: p, action: 'suspend' })}>
                            Suspend
                          </button>
                        ) : null}
                        {p.status === 'SUSPENDED' ? (
                          <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy !== null} onClick={() => reactivate(p)}>
                            Reactivate
                          </button>
                        ) : null}
                        {p.status !== 'DEREGISTERED' ? (
                          <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={busy !== null} onClick={() => setReasonFor({ party: p, action: 'deregister' })}>
                            Deregister
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreatePartyModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {reasonFor ? (
        <ReasonModal
          title={reasonFor.action === 'suspend' ? `Suspend ${reasonFor.party.name}` : `Deregister ${reasonFor.party.name}`}
          danger={reasonFor.action === 'deregister'}
          onClose={() => setReasonFor(null)}
          onSubmit={async (reason) => {
            await api.post(`/api/v1/m39/parties/${reasonFor.party.id}/${reasonFor.action}`, { reason });
            setReasonFor(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreatePartyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [symbolDescription, setSymbolDescription] = useState('');
  const [leaderNin, setLeaderNin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m39/parties', {
        name,
        abbreviation: abbreviation || undefined,
        symbolDescription: symbolDescription || undefined,
        leaderNin: leaderNin || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register party');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register a political party" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Seychelles Progressive Alliance" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Abbreviation (optional)">
            <input className="field-input" value={abbreviation} onChange={(e) => setAbbreviation(e.target.value)} placeholder="e.g. SPA" />
          </Field>
          <Field label="Leader NIN (optional)">
            <input className="field-input" value={leaderNin} onChange={(e) => setLeaderNin(e.target.value)} />
          </Field>
        </div>
        <Field label="Symbol description (optional)">
          <input className="field-input" value={symbolDescription} onChange={(e) => setSymbolDescription(e.target.value)} placeholder="e.g. Blue giant tortoise" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || name.trim().length < 2}>
            {busy ? 'Registering…' : 'Register party'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReasonModal({ title, danger, onClose, onSubmit }: { title: string; danger?: boolean; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(reason);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Statutory basis for this action" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={submit} disabled={busy || reason.trim().length < 3}>
            {busy ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
